import { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { MultiselectDropdown } from '@/shared/components/MultiselectDropdown/MultiselectDropdown'
import { TextField } from '@/shared/components/TextField/TextField'
import { pxToRem } from '@/shared/styles/themes'
import { Alert, Grid2 as Grid, styled, Typography } from '@mui/material'

import { DriverFormValues, GroupFormValues, RunFormValues } from '../models/OnboardingFormFields'

export type DriverSetupFormProps = {
  initialValues: DriverFormValues
  groupData: GroupFormValues
  runData: RunFormValues
  onSubmit: (data: DriverFormValues) => void
  isSubmitting: boolean
}

export const DriverSetupForm: React.FC<DriverSetupFormProps> = ({
  initialValues,
  groupData,
  runData,
  onSubmit,
  isSubmitting,
}) => {
  // Create options from the selected group and run
  const defaultGroupOptions = useMemo(() =>
    groupData.id && groupData.name ? [{
      label: groupData.name,
      value: groupData.id,
    }] : [],
  [groupData])

  const defaultRunOptions = useMemo(() =>
    runData.id && runData.name ? [{
      label: runData.name,
      value: runData.id,
    }] : [],
  [runData])

  // Create default values with pre-selected groups and runs
  const defaultValues = useMemo(() => {
    const values = { ...initialValues }

    // Add the group option if it exists and isn't already in the array
    if (defaultGroupOptions && !values.groupIds.some(g => g.value === defaultGroupOptions[0].value)) {
      values.groupIds = [...values.groupIds, ...defaultGroupOptions]
    }

    // Add the run option if it exists and isn't already in the array
    if (defaultRunOptions && !values.runIds.some(r => r.value === defaultRunOptions[0].value)) {
      values.runIds = [...values.runIds, ...defaultRunOptions]
    }

    return values
  }, [initialValues, defaultGroupOptions, defaultRunOptions])

  const { control, handleSubmit } = useForm<DriverFormValues>({ defaultValues })

  const onFormSubmit = (data: DriverFormValues) => {
    onSubmit(data)
  }

  return (
    <StyledForm id="onboarding-step-4" onSubmit={handleSubmit(onFormSubmit)}>
      <Typography variant="h2" sx={{ fontSize: pxToRem(24), fontWeight: 500, mb: 2 }}>
        Set Up Your First Driver
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Configure a driver profile who will be responsible for completing deliveries and pickups.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Drivers will use these credentials to log in to the driver mobile app and view their assigned runs.
      </Alert>

      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="login"
            control={control}
            rules={{ required: 'Login is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Login"
                placeholder="Enter Login"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Name"
                placeholder="Enter Driver Name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            name="password"
            control={control}
            rules={{
              required: 'Password is required',
              pattern: {
                value: /^\d+$/,
                message: 'Password must contain only numbers',
              },
              minLength: {
                value: 4,
                message: 'Password must be at least 4 digits',
              },
              maxLength: {
                value: 100,
                message: 'Password cannot exceed 100 digits',
              },
            }} render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Password"
                type="password"
                placeholder="Enter Driver Password"
                error={!!fieldState.error}
                helperText={fieldState.error?.message ? fieldState.error?.message : 'Enter a 4-12 digit numeric pin for the driver mobile app.'}
                fullWidth
                required
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            name="mobile"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Mobile"
                placeholder="Enter Driver's Mobile Number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            name="email"
            control={control}
            rules={{
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Please enter a valid email address',
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Email"
                placeholder="Enter Driver's Email"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            name="employeeNumber"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Employee Number"
                placeholder="Enter Employee Number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" gutterBottom marginTop={2}>
            Group & Run Assignment
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <MultiselectDropdown
            label="Groups"
            disabled
            placeholder="Select Groups"
            options={defaultGroupOptions}
            value={defaultGroupOptions}
            helperText={defaultGroupOptions ? `Your created group "${defaultGroupOptions[0].label}" is automatically assigned` : ''}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="runIds"
            control={control}
            rules={{ required: 'Please select at least one run' }}
            render={({ field }) => (
              <MultiselectDropdown
                {...field}
                label="Runs"
                disabled
                placeholder="Select Runs"
                options={defaultRunOptions}
                value={defaultRunOptions}
                helperText={defaultRunOptions ? `Your created run "${defaultRunOptions[0].label}" is automatically assigned` : ''}
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>
    </StyledForm>
  )
}

const StyledForm = styled('form')`
  width: 100%;
`